import {
  Component,
  OnDestroy,
  Renderer2,
  ElementRef,
  Input,
  EventEmitter,
  Output,
  ViewChild,
  SimpleChanges,
  NgZone,
  TemplateRef,
  ViewEncapsulation,
  AfterContentInit,
  OnInit,
  OnChanges,
  HostListener,
  Optional,
  Inject,
} from "@angular/core";
import { DOCUMENT } from "@angular/common";

import { takeUntil } from "rxjs/operators";
import { Subject, fromEvent, merge } from "rxjs";

const TOP_CSS_CLASS = "ng-select-top";
const BOTTOM_CSS_CLASS = "ng-select-bottom";

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: "ssg-popover",
  template: `
    <ng-content></ng-content>
    <ng-content select=".selectorfooter"></ng-content>
  `,
})
export class SSGPopOverComponent implements OnInit, OnChanges, OnDestroy, AfterContentInit {
  @Input() appendTo: string = "body";
  @Input() bufferAmount = 4;
  @Input() hasRefresh: boolean = false;
  @Input() currentPosition: string = null;
  @Input() position: string = "auto";

  @Output() update = new EventEmitter<any[]>();
  @Output() scroll = new EventEmitter<{ start: number; end: number }>();
  @Output() scrollToEnd = new EventEmitter<void>();
  @Output() outsideClick = new EventEmitter<void>();
  @Output() onSelectItem = new EventEmitter<void>();
  @Output() selectContains = new EventEmitter<void>();

  @ViewChild("content", { read: ElementRef, static: true }) contentElementRef: ElementRef;
  @ViewChild("scroll", { read: ElementRef, static: true }) scrollElementRef: ElementRef;
  @ViewChild("padding", { read: ElementRef, static: true }) paddingElementRef: ElementRef;

  private readonly _destroy$ = new Subject<void>();
  private readonly _dropdown: HTMLElement;
  private _select: HTMLElement;
  private _isScrolledToMarked = false;
  private _disposeScrollListener = () => {};
  private _disposeDocumentResizeListener = () => {};

  constructor(private _renderer: Renderer2, private _zone: NgZone, _elementRef: ElementRef, @Optional() @Inject(DOCUMENT) private _document: any) {
    this._dropdown = _elementRef.nativeElement;
  }

  @HostListener("mousedown", ["$event"])
  handleMousedown($event: MouseEvent) {
    const target = $event.target as HTMLElement;
    if (target.tagName === "INPUT") {
      return;
    }
    $event.preventDefault();
    $event.stopPropagation();
  }

  ngOnInit() {
    this._select = this._dropdown.parentElement;
    if (this._document) {
      merge(fromEvent(this._document, "touchstart", { capture: true }), fromEvent(this._document, "mousedown", { capture: true }))
        .pipe(takeUntil(this._destroy$))
        .subscribe(($event) => this._handleOutsideClick($event));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items) this._isScrolledToMarked = false;
  }

  async ngOnDestroy() {
    this._disposeDocumentResizeListener();
    this._disposeScrollListener();
    this._destroy$.next();
    this._destroy$.complete();
    if (this.appendTo) this._renderer.removeChild(this._dropdown.parentNode, this._dropdown);
  }

  ngAfterContentInit() {
    this._whenContentReady().then(() => {
      if (this.appendTo) {
        this._appendDropdown();
        this._handleDocumentResize();
      }
      this.updateDropdownPosition();
    });
  }

  refresh(): Promise<void> {
    return new Promise((resolve) => {
      this._zone.runOutsideAngular(() => {
        // this._window.requestAnimationFrame(() => {
        //   this._updateItems().then(resolve);
        // });
      });
    });
  }
  updateDropdownPosition() {
    this.currentPosition = this._calculateCurrentPosition(this._dropdown);
    if (this.currentPosition === "top") {
      this._renderer.addClass(this._dropdown, TOP_CSS_CLASS);
      this._renderer.removeClass(this._dropdown, BOTTOM_CSS_CLASS);
      this._renderer.addClass(this._select, TOP_CSS_CLASS);
      this._renderer.removeClass(this._select, BOTTOM_CSS_CLASS);
    } else {
      this._renderer.addClass(this._dropdown, BOTTOM_CSS_CLASS);
      this._renderer.removeClass(this._dropdown, TOP_CSS_CLASS);
      this._renderer.addClass(this._select, BOTTOM_CSS_CLASS);
      this._renderer.removeClass(this._select, TOP_CSS_CLASS);
    }

    this._dropdown.style.width = "fit-content";
    if (this.appendTo) {
      this._updateAppendedDropdownPosition();
    }
    this._dropdown.style.opacity = "1";
    this._dropdown.style.position = "fixed";
    this._dropdown.style.zIndex = "9999999";
    this._updateAppendedDropdownPosition();
  }

  private _handleOutsideClick($event: any) {
    if (this._select.contains($event.target)) {
      this.selectContains.emit();
      return;
    }

    if (this._dropdown.contains($event.target)) {
      this.onSelectItem.emit();
      return;
    }

    const path = $event.path || ($event.composedPath && $event.composedPath());
    if ($event.target && $event.target.shadowRoot && path && path[0] && this._select.contains(path[0])) {
      return;
    }

    this.outsideClick.emit();
  }

  private _handleScroll() {
    this._disposeScrollListener = this._renderer.listen(this.scrollElementRef.nativeElement, "scroll", () => {
      this.refresh();
    });
  }

  private _handleDocumentResize() {
    if (!this.appendTo) {
      return;
    }
    this._disposeDocumentResizeListener = this._renderer.listen("window", "resize", () => {
      this._updateAppendedDropdownPosition();
    });
  }

  private _calculateCurrentPosition(dropdownEl: HTMLElement) {
    if (this.position !== "auto") {
      return this.position;
    }
    const selectRect: ClientRect = this._select.getBoundingClientRect();
    const offsetTop = selectRect.top + window.pageYOffset;
    if (offsetTop > document.body.getBoundingClientRect().height / 2) {
      return "top";
    } else {
      return "bottom";
    }
  }

  private _appendDropdown() {
    const parent = document.querySelector(this.appendTo);
    if (!parent) {
      throw new Error(`appendTo selector ${this.appendTo} did not found any parent element`);
    }
    parent.appendChild(this._dropdown);
  }

  private _updateAppendedDropdownPosition() {
    const parent = document.querySelector(this.appendTo) || document.body;
    this._dropdown.style.width = "fit-content";
    this._dropdown.style.display = "none";
    const selectRect: ClientRect = this._select.getBoundingClientRect();
    const boundingRect = parent.getBoundingClientRect();
    this._dropdown.style.display = "";
    const offsetTop = selectRect.top - boundingRect.top;
    let bod = document.body.getBoundingClientRect();
    let dropdownEl = this._dropdown?.getBoundingClientRect();
    let offsetLeft = selectRect.left - boundingRect.left >= 0 ? selectRect.left - boundingRect.left : 0;
    let offsetRight = bod.width - selectRect.right;
    if (offsetLeft < 0) offsetLeft = 0;
    const topDelta = this.currentPosition === "bottom" ? selectRect.height : -this._dropdown.clientHeight;
    if (offsetTop + topDelta > bod.height) {
      this._dropdown.style.top = bod.height + "px";
    } else if (offsetTop + topDelta < 0) {
      this._dropdown.style.top = 0 + "px";
    } else this._dropdown.style.top = offsetTop + topDelta + "px";

    this._dropdown.style.bottom = "auto";
    this._dropdown.style.left = dropdownEl?.left + dropdownEl?.width > boundingRect.width / 2 ? null : offsetLeft + "px";
    this._dropdown.style.right = dropdownEl?.left + dropdownEl?.width > boundingRect.width / 2 ? offsetRight + "px" : null;
    this._dropdown.style.position = "fixed";
    this._dropdown.style.zIndex = "1000000000000000000000000";
  }

  private _whenContentReady(): Promise<void> {
    return Promise.resolve();
  }
}
